import React, { useState } from "react";
import { motion } from "framer-motion";
import { useFetch } from "@/lib/api";
import { PageHeader, Panel, StatusChip, LoadingState, DemoBadge } from "@/components/app/primitives";
import { LiveAdoptionPlansPanel } from "@/components/app/LiveSummaryPanel";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const phaseTone = (s) => s === "complete" ? { chip: "validated", label: "Complete", ring: "border-[--green] bg-[--green-soft]", text: "text-[--green]" }
  : s === "current" ? { chip: "crimson", label: "In progress", ring: "border-[--six-crimson] bg-[--six-crimson-soft]", text: "text-[#E86A6A]" }
  : { chip: "neutral", label: "Upcoming", ring: "border-[--hairline] bg-[--shell-2]", text: "text-[--tx-3]" };

const ACCEL_STATUS = {
  in_play: { label: "In play at Brightspeed", tone: "validated" },
  queued: { label: "Queued (90-day)", tone: "ai" },
  available: { label: "Available", tone: "neutral" },
  use_with_care: { label: "Use with care", tone: "risk" },
};

const ReadinessDots = ({ value }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <span key={n} className={`w-2.5 h-2.5 rounded-full ${n <= value ? (value >= 4 ? "bg-[--green]" : value >= 3 ? "bg-[--ai]" : "bg-[--amber]") : "bg-[--shell-3]"}`} />
    ))}
  </div>
);

const supportTone = (s) => s === "Champion" ? "validated" : s.startsWith("Supportive") ? "ai" : s === "Mixed" ? "risk" : s === "Skeptical" ? "danger" : "neutral";

export default function ChangeManagementPage() {
  const { data: cm, loading } = useFetch("/change-management");
  const [phaseIdx, setPhaseIdx] = useState(2);
  const [accelFilter, setAccelFilter] = useState("all");
  const [selectedAccel, setSelectedAccel] = useState(null);

  if (loading || !cm) return <LoadingState message="Loading the change approach..." />;
  const phase = cm.phases[phaseIdx];
  const pt = phaseTone(phase.brightspeed_status);
  const accels = cm.accelerators.filter((a) => accelFilter === "all" || a.status === accelFilter);

  return (
    <div data-testid="change-page">
      <PageHeader
        kicker="SIX methodology - accelerating adoption"
        title="Change Management"
        subtitle="People change is usually the toughest part of any transformation. SIX manages it by plan and design - purposefully, versus improvisation - to win the hearts and minds of stakeholders and accelerate adoption."
        right={<DemoBadge />}
      />

      <LiveAdoptionPlansPanel />

      {/* Definition + objectives */}
      <div className="grid lg:grid-cols-3 gap-4 mb-5">
        <Panel title="Organizational Change Management - defined" className="lg:col-span-2" testId="change-definition-panel">
          <p className="text-[13px] text-[--tx-2] leading-relaxed">{cm.definition}</p>
          <p className="text-xs text-[--tx-3] leading-relaxed mt-2.5 pt-2.5 border-t border-[--divider]">{cm.posture}</p>
        </Panel>
        <Panel title="Objectives" testId="change-objectives-panel">
          <div className="space-y-2.5">
            {cm.objectives.map((o, i) => (
              <div key={o.name} className="flex gap-2.5">
                <span className="font-display text-lg font-bold text-[#E86A6A] shrink-0 leading-none pt-0.5">{i + 1}</span>
                <div>
                  <div className="text-xs font-semibold text-[--tx-1]">{o.name}</div>
                  <p className="text-[11px] text-[--tx-3] leading-relaxed mt-0.5">{o.text}</p>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* Phases I-VI */}
      <Panel title="The SIX Change Management approach - six phases, iterative and agile" className="mb-5" testId="change-phases-panel">
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4">
          {cm.phases.map((p, i) => {
            const t = phaseTone(p.brightspeed_status);
            const isSel = i === phaseIdx;
            return (
              <button key={p.numeral} onClick={() => setPhaseIdx(i)} data-testid={`change-phase-${p.numeral}`}
                className={`rounded-lg border-2 ${t.ring} ${isSel ? "ring-2 ring-[--ring]" : ""} px-2 py-2.5 text-center transition-all hover:scale-[1.02] active:scale-[0.99]`}>
                <div className={`font-display text-sm font-bold ${t.text}`}>{p.numeral}</div>
                <div className={`text-[11px] font-semibold ${isSel ? "text-[--tx-1]" : "text-[--tx-2]"}`}>{p.name}</div>
              </button>
            );
          })}
        </div>
        <motion.div key={phase.numeral} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15 }}>
          <div className="grid lg:grid-cols-3 gap-4" data-testid="change-phase-detail">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <h4 className="font-display text-sm font-semibold text-[--tx-1]">Phase {phase.numeral} - {phase.name}</h4>
                <StatusChip tone={pt.chip}>{pt.label}</StatusChip>
              </div>
              <p className="text-xs text-[--tx-2] leading-relaxed">{phase.text}</p>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-1.5">Deliverables</div>
              <div className="flex flex-wrap gap-1.5">
                {phase.deliverables.map((d) => <StatusChip key={d} tone="neutral">{d}</StatusChip>)}
              </div>
            </div>
            <div className="rounded-lg bg-[--shell-2] border border-[--divider] p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#E86A6A] mb-1">At Brightspeed</div>
              <p className="text-xs text-[--tx-2] leading-relaxed">{phase.brightspeed_note}</p>
            </div>
          </div>
        </motion.div>
      </Panel>

      {/* Resistance + keys */}
      <div className="grid lg:grid-cols-5 gap-4 mb-5">
        <Panel title="Why people resist change" className="lg:col-span-2" testId="change-resistance-panel">
          <div className="space-y-3">
            {cm.resistance_factors.map((r) => (
              <div key={r.name} className="rounded-lg bg-[--shell-2] border border-[--divider] p-3">
                <div className="text-xs font-semibold text-[--amber] mb-0.5">{r.name}</div>
                <p className="text-[11px] text-[--tx-2] leading-relaxed">{r.text}</p>
                <p className="text-[11px] text-[--tx-3] leading-relaxed mt-1.5 pt-1.5 border-t border-[--divider]"><span className="text-[#E86A6A] font-mono text-[9px] uppercase tracking-wider">Brightspeed </span>{r.brightspeed_lens}</p>
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="Keys to change management success" className="lg:col-span-3" testId="change-keys-panel">
          <div className="grid md:grid-cols-2 gap-2.5">
            {cm.keys_to_success.map((k, i) => (
              <div key={k.name} className="flex gap-2.5 rounded-lg bg-[--shell-2] border border-[--divider] p-3">
                <span className="font-display text-lg font-bold text-[--ai] shrink-0 leading-none pt-0.5">{i + 1}</span>
                <div>
                  <div className="text-xs font-semibold text-[--tx-1]">{k.name}</div>
                  <p className="text-[11px] text-[--tx-3] leading-relaxed mt-0.5">{k.text}</p>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* Accelerators */}
      <Panel title={`Change Accelerators - the levers we can pull (${cm.accelerators.length})`} className="mb-5" testId="change-accelerators-panel"
        right={
          <div className="flex items-center gap-1.5 flex-wrap">
            {["all", "in_play", "queued", "available", "use_with_care"].map((f) => (
              <button key={f} onClick={() => setAccelFilter(f)} data-testid={`accel-filter-${f}`}
                className={`rounded-lg px-2.5 py-1 text-[11px] font-medium border transition-colors ${accelFilter === f ? "border-[--six-crimson] bg-[--six-crimson-soft] text-[--tx-1]" : "border-[--hairline] bg-[--shell-3] text-[--tx-3] hover:text-[--tx-1]"}`}>
                {f === "all" ? "All" : ACCEL_STATUS[f].label}
              </button>
            ))}
          </div>
        }>
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-2.5">
          {accels.map((a) => (
            <button key={a.name} onClick={() => setSelectedAccel(a)} data-testid={`accel-card-${a.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
              className="text-left rounded-xl border border-[--hairline] bg-[--panel-dark] p-3 hover:border-[--heat-3] transition-colors active:scale-[0.99]">
              <div className="flex items-start justify-between gap-2 mb-1">
                <h4 className="text-xs font-semibold text-[--tx-1] leading-snug">{a.name}</h4>
                <StatusChip tone={ACCEL_STATUS[a.status].tone} className="shrink-0 !text-[9px]">{ACCEL_STATUS[a.status].label}</StatusChip>
              </div>
              <p className="text-[11px] text-[--tx-3] leading-snug line-clamp-2">{a.definition}</p>
            </button>
          ))}
        </div>
      </Panel>

      {/* Stakeholder readiness */}
      <Panel title="Stakeholder readiness assessment - Brightspeed (Phase III output)" className="mb-5" testId="change-readiness-panel"
        right={<DemoBadge />}>
        <div className="overflow-x-auto thin-scroll">
          <table className="w-full text-left dense-table" data-testid="readiness-table">
            <thead>
              <tr className="border-b border-[--divider]">
                <th className="py-2 pr-3">Stakeholder group</th>
                <th className="py-2 px-3">Readiness</th>
                <th className="py-2 px-3">Support level</th>
                <th className="py-2 px-3 hidden lg:table-cell">Primary barrier</th>
                <th className="py-2 px-3 hidden md:table-cell">Engagement play</th>
                <th className="py-2 pl-3">Priority</th>
              </tr>
            </thead>
            <tbody>
              {cm.stakeholder_readiness.map((s) => (
                <tr key={s.group} className="border-b border-[--divider] last:border-0" data-testid={`readiness-row-${s.group.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
                  <td className="py-2.5 pr-3 text-[13px] font-medium text-[--tx-1]">{s.group}</td>
                  <td className="py-2.5 px-3"><ReadinessDots value={s.readiness} /></td>
                  <td className="py-2.5 px-3"><StatusChip tone={supportTone(s.support)}>{s.support}</StatusChip></td>
                  <td className="py-2.5 px-3 hidden lg:table-cell text-[11px] text-[--tx-3] max-w-[240px]">{s.barrier}</td>
                  <td className="py-2.5 px-3 hidden md:table-cell text-[11px] text-[--tx-2] max-w-[280px]">{s.play}</td>
                  <td className="py-2.5 pl-3"><StatusChip tone={s.priority === "Sequence carefully" ? "risk" : "neutral"}>{s.priority}</StatusChip></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {/* Adoption metrics + PLP alignment */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Panel title="Adoption metrics - designed into every pilot" className="lg:col-span-2" testId="change-adoption-metrics-panel">
          <div className="grid md:grid-cols-2 gap-2.5">
            {cm.adoption_metrics.map((m) => (
              <div key={m.metric} className="rounded-lg bg-[--shell-2] border border-[--divider] p-3">
                <div className="text-xs font-semibold text-[--tx-1] mb-1">{m.metric}</div>
                <div className="flex items-center gap-2 text-[10px] mb-1">
                  <span className="text-[--tx-3]">Base: <span className="font-mono text-[--tx-2]">{m.baseline}</span></span>
                  <span className="font-mono text-[--green]">&rarr; {m.target}</span>
                  <span className="ml-auto font-mono text-[--ai]">{m.checkpoint}</span>
                </div>
                <p className="text-[10px] text-[--amber] leading-snug">{m.guard}</p>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-[--tx-3] mt-3 pt-2.5 border-t border-[--divider] leading-relaxed">
            Adoption occurs when desired actions follow the message - not when the message is heard. If adherence drops below threshold, the pilot pauses and gets fixed rather than accumulating bad data. No silent failure.
          </p>
        </Panel>
        <Panel title="Alignment with the Program Leadership Process" testId="change-plp-panel">
          <p className="text-xs text-[--tx-2] leading-relaxed">{cm.plp_alignment}</p>
          <div className="flex flex-wrap gap-1.5 mt-3">
            <StatusChip tone="crimson">Sponsorship (DIM-02)</StatusChip>
            <StatusChip tone="crimson">Communications (DIM-08)</StatusChip>
            <StatusChip tone="neutral">Change Control</StatusChip>
            <StatusChip tone="neutral">Workplanning</StatusChip>
          </div>
        </Panel>
      </div>

      <Sheet open={!!selectedAccel} onOpenChange={(o) => !o && setSelectedAccel(null)}>
        <SheetContent className="bg-[--shell-2] border-[--hairline] w-full sm:max-w-md overflow-y-auto thin-scroll" data-testid="accel-detail-drawer">
          {selectedAccel && (
            <>
              <SheetHeader>
                <SheetTitle className="text-[--tx-1] font-display">{selectedAccel.name}</SheetTitle>
                <SheetDescription className="text-[--tx-3] text-xs">Change Accelerator</SheetDescription>
              </SheetHeader>
              <div className="space-y-4 mt-4">
                <StatusChip tone={ACCEL_STATUS[selectedAccel.status].tone}>{ACCEL_STATUS[selectedAccel.status].label}</StatusChip>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-1">The technique</div>
                  <p className="text-[13px] text-[--tx-2] leading-relaxed">{selectedAccel.definition}</p>
                </div>
                <div className="rounded-lg bg-[--panel-dark] border border-[--divider] p-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#E86A6A] mb-1">Applied at Brightspeed</div>
                  <p className="text-xs text-[--tx-2] leading-relaxed">{selectedAccel.brightspeed_play}</p>
                </div>
                {selectedAccel.related?.length > 0 && (
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-1">Linked records</div>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedAccel.related.map((r) => <span key={r} className="font-mono text-[10px] rounded bg-[--shell-3] border border-[--divider] px-1.5 py-0.5 text-[--ai]">{r}</span>)}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
